import { toast } from "sonner";
import {
  FaCheck,
  FaTimes,
  FaExclamation,
  FaInfo,
} from "react-icons/fa";
import React from "react";

type ToastOptions = {
  description?: string;
  duration?: number;
};

// Composant Toast personnalisé Arc-style (même style que Popover/Tooltip)
const ArcToast: React.FC<{
  type: "success" | "error" | "warning" | "info";
  message: string;
  description?: string;
  onDismiss?: () => void;
}> = ({ type, message, description, onDismiss }) => {
  const config = {
    success: {
      icon: <FaCheck className="w-3 h-3" />,
      iconBg: "bg-success",
      iconColor: "text-white",
    },
    error: {
      icon: <FaTimes className="w-3 h-3" />,
      iconBg: "bg-error",
      iconColor: "text-white",
    },
    warning: {
      icon: <FaExclamation className="w-3 h-3" />,
      iconBg: "bg-warning-dark",
      iconColor: "text-white",
    },
    info: {
      icon: <FaInfo className="w-2.5 h-2.5" />,
      iconBg: "bg-info",
      iconColor: "text-white",
    },
  };

  const { icon, iconBg, iconColor } = config[type];

  return (
    <div className="flex items-start gap-3 bg-surface-elevated border border-arc-border rounded-arc-md px-4 py-3 min-w-[300px] max-w-[400px] shadow-arc-lg">
      {/* Icon avec cercle coloré */}
      <div className={`flex-shrink-0 w-6 h-6 rounded-full ${iconBg} ${iconColor} flex items-center justify-center`}>
        {icon}
      </div>
      
      {/* Contenu */}
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="font-medium text-sm text-content-primary leading-tight">{message}</p>
        {description && (
          <p className="text-xs text-content-secondary mt-1 leading-relaxed">{description}</p>
        )}
      </div>

      {/* Bouton close */}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 -mr-1 -mt-0.5 rounded-full text-content-tertiary hover:text-content-secondary hover:bg-surface-secondary transition-colors duration-150"
        >
          <FaTimes className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default () => {
  const addError = (message?: string, options?: ToastOptions) => {
    toast.custom(
      (t) => (
        <ArcToast
          type="error"
          message={message ?? "Une erreur est arrivée"}
          description={options?.description}
          onDismiss={() => toast.dismiss(t)}
        />
      ),
      {
        duration: options?.duration ?? 4000,
      }
    );
  };

  const addSuccess = (message?: string, options?: ToastOptions) => {
    toast.custom(
      (t) => (
        <ArcToast
          type="success"
          message={message ?? "Succès !"}
          description={options?.description}
          onDismiss={() => toast.dismiss(t)}
        />
      ),
      {
        duration: options?.duration ?? 4000,
      }
    );
  };

  const addInfo = (message: string, options?: ToastOptions) => {
    toast.custom(
      (t) => (
        <ArcToast
          type="info"
          message={message ?? "Info"}
          description={options?.description}
          onDismiss={() => toast.dismiss(t)}
        />
      ),
      {
        duration: options?.duration ?? 4000,
      }
    );
  };

  const addWarning = (message?: string, options?: ToastOptions) => {
    toast.custom(
      (t) => (
        <ArcToast
          type="warning"
          message={message ?? "Attention"}
          description={options?.description}
          onDismiss={() => toast.dismiss(t)}
        />
      ),
      {
        duration: options?.duration ?? 4000,
      }
    );
  };

  /**
   * Toast avec promesse - affiche automatiquement loading/success/error
   */
  const addPromise = <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: unknown) => string);
    }
  ) => {
    return toast.promise(promise, {
      loading: messages.loading,
      success: (data) => {
        const msg = typeof messages.success === "function" 
          ? messages.success(data) 
          : messages.success;
        return <ArcToast type="success" message={msg} />;
      },
      error: (err) => {
        const msg = typeof messages.error === "function" 
          ? messages.error(err) 
          : messages.error;
        return <ArcToast type="error" message={msg} />;
      },
    });
  };

  /**
   * Fermer un toast spécifique ou tous les toasts
   */
  const dismiss = (toastId?: string | number) => {
    toast.dismiss(toastId);
  };

  return { 
    addError, 
    addSuccess, 
    addInfo, 
    addWarning, 
    addPromise,
    dismiss,
  } as const;
};
